import { useEffect, useState } from "react";
import { Menu } from "../../../components/Menu";
import { useParams, useNavigate } from "react-router-dom";

import { useAppConfig } from "../../../hooks/useAppConfig";
import { ConfigType } from "../../../contexts/AppConfigContext";
import { Loading } from "../../../components/Loading";
import api from "../../../services/api";
import "./index.css";
import { usePluginspace } from "../../../hooks/usePluginspace";
import {
  calculatePriceFractionalToFractional,
  floatToPrice,
} from "../../../functions/price_functions";
import { QuantityButton } from "../../../components/QuantityButton";
import { useOrder } from "../../../hooks/useOrder";
import Swal from "sweetalert2";
import { MainButton } from "../../../components/MainButton";
import {
  AdditionalType,
  ItemFractionalType,
  OrderType,
  ProductType,
} from "../../../contexts/OrderContext";
import { useProduct } from "../../../hooks/useProduct";
import { ProductFoodType } from "../../../contexts/ProductsContext";
import { ModalProdutoCarrinho } from "../../../components/ModalProdutoCarrinho/index";

type OptionalType = {
  id: number;
  name: string;
  required: number;
  type: string;
  description?: string;
  max?: number;
  items: {
    id: number;
    max: number | null;
    selected?: boolean;
    quantity?: number;
    product: {
      id: string;
      name: string;
      code: string;
      variations: VariationType[];
    };
  }[];
};

export type FractionalType = {
  max: number;
  showPrice: boolean;
  showDescription: boolean;
  typePrice: "proportional" | "biggest";
  items: ItemFractionalType[];
};

export type VariationType = {
  id: string;
  status: boolean;
  referenceCode: string;
  sku: string;
  price: number;
  promotionalPrice: number;
  inventory: number;
  minimumStock: number;
  context: string;
  img: string;
  locationId: number;
};

type ImageType = {
  path: string;
  imgsuffix: string;
  productId: number;
  typeimg: string;
  order: number;
};

export function FoodProduct() {
  const { pluginspace_id, product_id } = useParams();
  const navigate = useNavigate();
  const { pushToOrder, getOrder } = useOrder();
  const { productFoodSelected } = useProduct();
  const { getAppConfig } = useAppConfig();
  const { getToken, registerAccess, getVerifyShift } = usePluginspace();
  const [config, setConfig] = useState<ConfigType | void>();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState<ProductFoodType | undefined>();
  const [imagePath, setImagePath] = useState<string | undefined>();
  const [optionals, setOptionals] = useState<OptionalType[]>([]);
  const [fractional, setFractional] = useState<FractionalType>();
  const [price, setPrice] = useState(0);
  const [totalSuccess, setTotalSuccess] = useState(false);
  const [validShift, setValidShift] = useState(false);
  const [observation, setObservation] = useState<string | undefined>();
  const [quantity, setQuantity] = useState(1);
  const [loadingImage, setLoadingImage] = useState(true);
  const [orderFinded, setOrderFinded] = useState<OrderType | null>(null);
  const [sku, setSku] = useState("");
  const [inventory, setInventory] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [productModal, setProductModal] = useState<any | null>(null);

  useEffect(() => {
    registerAccess("food-product");
    setProduct(undefined);
    const fetchData = async () => {
      setLoading(true);
      await getConfig();
      await getProduct();
      setValidShift(await getVerifyShift());
      setLoading(false);
    };
    fetchData();
  }, [product_id]);

  async function getConfig() {
    setConfig(await getAppConfig());
    const newOrder = await getOrder();
    setOrderFinded(newOrder);
  }

  useEffect(() => {
    if (product) {
      if (product.images && product.images.length > 0) {
        const imgFind = product.images[0];
        if (imgFind) {
          const imgProduct =
            imgFind.path.split("://")[1] === "s3.amazonaws.com/img.ninegrid.com"
              ? imgFind.path + "/" + imgFind.imgsuffix
              : imgFind.path;
          setImagePath(imgProduct);
        }
      }

      if (
        product.type === "fractional" &&
        product.quantityFractional &&
        product.typeFractional &&
        product.fractions &&
        product.fractions.length > 0
      ) {
        setFractional({
          max: product.quantityFractional,
          showPrice: product.showFractionalPrice
            ? product.showFractionalPrice
            : false,
          showDescription: product.showFractionalDescription
            ? product.showFractionalDescription
            : false,
          typePrice: product.typeFractional,
          items: product.fractions
            .sort((a, b) => a.id - b.id)
            .map((fract) => ({
              id: parseInt(fract.productFractionalId),
              name: fract.item[0] ? fract.item[0].name : "",
              code: fract.item[0] ? fract.item[0].code : "",
              description: fract.item[0] ? fract.item[0].description : "",
              price: fract.variations[0].price,
              quantity: 0,
            })),
        });
      }
      setOptionals(product.optionals ?? []);
      setPrice(product.variations ? product.variations[0].price ?? 0 : 0);
    }
  }, [product]);

  useEffect(() => {
    if (optionals.length || fractional) {
      recalculatePrice();
    }
    const successOptional = totalValidate();
    const successFracional = validateFractional();
    setTotalSuccess(successOptional && successFracional);
  }, [optionals, fractional]);

  async function getProduct() {
    if (productFoodSelected) {
      if (
        productFoodSelected.variations &&
        productFoodSelected.variations[0] &&
        productFoodSelected.variations[0].inventory &&
        productFoodSelected.variations[0].sku
      ) {
        setSku(productFoodSelected.variations[0].sku);
        setInventory(productFoodSelected.variations[0].inventory);
      }
      Array.isArray(productFoodSelected?.optionals) &&
        productFoodSelected.optionals.forEach((optional) =>
          optional.items.forEach(
            (item) => item.quantity && delete item.quantity
          )
        );

      setProduct(Object.assign(productFoodSelected));
      return;
    }
    try {
      const response = await api.get(`api/product-food/${product_id}`, {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      });
      if (
        response.data.data[0].variations &&
        response.data.data[0].variations[0] &&
        response.data.data[0].variations[0].inventory &&
        response.data.data[0].variations[0].sku
      ) {
        setSku(response.data.data[0].variations[0].sku);
        setInventory(response.data.data[0].variations[0].inventory);
      }
      setProduct(response.data.data[0]);
    } catch (error) {
      console.log(error);
    }
  }

  const recalculatePrice = () => {
    let _price =
      product && product.variations ? product.variations[0].price : 0;

    if (product?.type === "fractional" && fractional) {
      _price = 0;
      if (fractional.typePrice === "proportional") {
        let qtdToDivide = 0;
        fractional.items.forEach((item) => {
          if (item.quantity > 0) {
            qtdToDivide += item.quantity;
          }
        });
        if (qtdToDivide === 3) {
          let indexItem = 0;
          let newItemFractions: ItemFractionalType[] = [];
          fractional.items.forEach((item) => {
            if (item.quantity > 0) {
              for (let i = 0; i < item.quantity; i++) {
                const newItem = { ...item };
                newItem.quantity = 1;
                newItemFractions.push(newItem);
              }
            }
          });
          newItemFractions.forEach((item) => {
            if (item.quantity > 0) {
              indexItem += item.quantity;
              let multiplyItem =
                Math.trunc((item.quantity / qtdToDivide) * 1000) / 1000;
              if (indexItem === qtdToDivide) {
                multiplyItem += 0.001;
              }
              let value = parseFloat((item.price * multiplyItem).toFixed(2));
              _price += value;
            }
          });
        } else {
          fractional.items.forEach((item) => {
            if (item.quantity > 0) {
              const priceItemCents = item.price * 100;
              let value = parseFloat((priceItemCents / qtdToDivide).toFixed(2));
              _price += (value * item.quantity) / 100;
            }
          });
          let quantityProduct = product.quantity ? product.quantity : 0;
          _price = (_price / fractional.max) * quantityProduct;
        }
      } else if (fractional.typePrice === "biggest") {
        fractional.items.forEach((item) => {
          if (item.price > _price && item.quantity > 0) _price = item.price;
        });
        let quantityProduct = product.quantity ? product.quantity : 0;
        _price = _price * quantityProduct;
      }
    }
    optionals.forEach((optional) => {
      optional.items.forEach((item) => {
        if (item.quantity) {
          _price += item.quantity * item.product.variations[0].price;
        }
        if (item.selected) {
          _price += item.product.variations[0].price;
        }
      });
    });
    setPrice(_price);
  };

  const totalValidate = () => {
    let _totalSuccess = true;
    optionals.map((optional, i) => {
      if (optional.required) {
        if (optional.type === "amount" && _totalSuccess) {
          let quantity = 0;
          optional.items.map((item) => {
            if (item.quantity) {
              quantity += item.quantity;
            }
          });
          _totalSuccess = quantity > 0;
        }
        if (optional.type === "checkbox" && _totalSuccess) {
          const selecteds = optional.items.filter(
            (c) => c.selected === true
          ).length;
          _totalSuccess = selecteds > 0;
        }
        if (optional.type === "radio" && _totalSuccess) {
          _totalSuccess =
            optional.items.filter((c) => c.selected === true).length > 0;
        }
      }
    });
    return _totalSuccess;
  };

  const validateFractional = () => {
    let _totalSuccess = true;
    if (fractional) {
      let quantity = 0;
      fractional.items.map((item) => {
        if (item.quantity) {
          quantity += item.quantity;
        }
      });
      // _totalSuccess = quantity === fractional.max;
      _totalSuccess = quantity > 0;
      return _totalSuccess;
    }
    return true;
  };

  const renderRadio = (selected: boolean = false) => {
    return selected ? (
      <svg
        width="28"
        height="28"
        viewBox="0 0 28 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="14" cy="14" r="13.5" stroke="#636363" />
        <circle cx="14" cy="14" r="13.5" stroke="#636363" />
        <circle cx="14" cy="14" r="7" fill="#940C0C" />
      </svg>
    ) : (
      <svg
        width="28"
        height="28"
        viewBox="0 0 28 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="14" cy="14" r="13.5" stroke="#636363" />
        <circle cx="14" cy="14" r="13.5" stroke="#636363" />
      </svg>
    );
  };

  const selectRadio = (optionalIndex: number, itemIndex: number) => {
    let _optionals = [...optionals];
    _optionals[optionalIndex].items.forEach((item, i) => {
      if (_optionals[optionalIndex].required) {
        item.selected = i === itemIndex;
      } else item.selected = i === itemIndex && !item.selected;
    });
    setOptionals(_optionals);
  };

  const selectCheckbox = (optionalIndex: number, itemIndex: number) => {
    let _optionals = [...optionals];
    const selecteds = _optionals[optionalIndex].items.filter(
      (c) => c.selected === true
    ).length;
    const max = _optionals[optionalIndex].max;
    const selected = _optionals[optionalIndex].items[itemIndex].selected;
    if (!max || max === 0 || selected || max > selecteds) {
      _optionals[optionalIndex].items.map((item, i) => {
        if (i === itemIndex) {
          item.selected = !item.selected;
        }
      });
    }
    setOptionals(_optionals);
  };

  const changeQuantity = (
    optionalIndex: number,
    itemIndex: number,
    quantity: number
  ) => {
    let _optionals = [...optionals];
    let quantities = 0;
    for (var key in _optionals[optionalIndex].items) {
      quantities += _optionals[optionalIndex].items[key].quantity || 0;
    }
    const max = _optionals[optionalIndex].max;
    const maxItem = _optionals[optionalIndex].items[itemIndex].max
      ? _optionals[optionalIndex].items[itemIndex].max
      : 0;
    const quantityOptional = _optionals[optionalIndex].items[itemIndex].quantity
      ? _optionals[optionalIndex].items[itemIndex].quantity
      : 0;

    if (
      !max ||
      max === 0 ||
      (max >= quantities + quantity &&
        (!maxItem ||
          maxItem === 0 ||
          maxItem >= quantity + (quantityOptional ? quantityOptional : 0)))
    ) {
      let _quantity = _optionals[optionalIndex].items[itemIndex].quantity || 0;
      if (_quantity + quantity >= 0) {
        _optionals[optionalIndex].items[itemIndex].quantity =
          _quantity + quantity;
        setOptionals(_optionals);
      }
    }
  };

  const changeQuantityFractional = (itemIndex: number, quantity: number) => {
    if (fractional) {
      let fractionalItems = [...fractional.items];
      let quantities = 0;
      fractionalItems.forEach((item) => {
        quantities += item.quantity;
      });
      const max = fractional.max;
      if (!max || max === 0 || max >= quantities + quantity) {
        let _quantity = fractionalItems[itemIndex].quantity || 0;
        if (_quantity + quantity >= 0) {
          fractionalItems[itemIndex].quantity = _quantity + quantity;
          setFractional({
            max: fractional.max,
            showPrice: fractional.showPrice,
            showDescription: fractional.showDescription,
            typePrice: fractional.typePrice,
            items: fractionalItems,
          });
        }
      }
    }
  };

  function addCart() {
    if (config && !config.content.pdv.showOutStock) {
      const stockOk = verifyMaxStockWithCart(quantity);
      if (!stockOk) {
        Swal.fire({
          icon: "warning",
          title: "Estoque máximo atingido",
          text: "Não é possível adicionar o produto ao carrinho",
        });
        return;
      }
    }
    if (product && product.variations) {
      let prod: ProductType = {
        id: product_id ? parseInt(product_id) : 2,
        name: product.name,
        image: imagePath ?? "",
        unitPrice: product.variations[0].price,
        promotionalPrice: product.variations[0].promotionalPrice,
        quantity: quantity,
        stock: product.variations[0].inventory,
        sku: product.variations[0].sku,
        variation: [],
        code: product.code,
        locationId: product.variations[0].locationId,
        pluginspaceHash: pluginspace_id ? pluginspace_id : "",
        observation,
        type: product.type,
        typeFractional: fractional ? fractional.typePrice : "",
        outStock: false,
        measures: null,
      };

      let additionals: AdditionalType[] = [];

      optionals.map((optional) => {
        optional.items.map((item) => {
          if (item.quantity || item.selected) {
            additionals.push({
              code: item.product.code,
              name: item.product.name,
              quantity: item.quantity ?? 1,
              price: item.product.variations[0].price,
            });
          }
        });
      });

      prod.additionals = additionals;
      if (
        product.type === "fractional" &&
        fractional?.items &&
        fractional.items.length > 0
      ) {
        const fractions = fractional.items.filter((item) => item.quantity > 0);
        let fractionsToAdd: ItemFractionalType[] = [];

        fractions.forEach((fraction) => {
          if (fraction.quantity > 0) {
            for (let i = 0; i < fraction.quantity; i++) {
              let fractToAdd = { ...fraction };
              fractToAdd.quantity = 1;
              fractionsToAdd.push(fractToAdd);
            }
          }
        });

        prod.fractions = fractionsToAdd;
      }
      pushToOrder(prod);
      setProductModal(prod);
      setModalOpen(true);
    } else {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível encontrar o produto!",
      });
    }
  }

  function calculatePrice() {
    if (product && product.variations) {
      let prod: ProductType = {
        id: product_id ? parseInt(product_id) : 2,
        name: product.name,
        image: imagePath ?? "",
        unitPrice: product.variations[0].price,
        promotionalPrice: product.variations[0].promotionalPrice,
        quantity: quantity,
        stock: product.variations[0].inventory,
        sku: product.variations[0].sku,
        variation: [],
        code: product.code,
        locationId: product.variations[0].locationId,
        pluginspaceHash: pluginspace_id ? pluginspace_id : "",
        observation,
        outStock: false,
        measures: null,
      };

      let additionals: AdditionalType[] = [];

      optionals.map((optional) => {
        optional.items.map((item) => {
          if (item.quantity || item.selected) {
            additionals.push({
              code: item.product.code,
              name: item.product.name,
              quantity: item.quantity ?? 1,
              price: item.product.variations[0].price,
            });
          }
        });
      });

      prod.additionals = additionals;
      let priceAdditionals = 0;
      if (prod.additionals) {
        prod.additionals.map(
          (additional) =>
            (priceAdditionals += additional.quantity * additional.price)
        );
      }

      if (product?.type === "fractional" && fractional) {
        return floatToPrice(
          calculatePriceFractionalToFractional(fractional) * prod.quantity +
            priceAdditionals * prod.quantity
        );
      }

      return floatToPrice(
        prod.unitPrice * prod.quantity + priceAdditionals * prod.quantity
      );
    } else {
      return "R$ 0,00";
    }
  }

  const imageStyle = () => {
    switch (config?.content.pdv.typeImage) {
      case "round":
        return "image-50Rounded";
      case "rounded":
        return "image-25Rounded";
      case "squarde":
        return "image-0Rounded";
      default:
        return "image-50Rounded";
    }
  };

  function verifyMaxStockWithCart(value: number) {
    let stockOk = true;
    if (orderFinded) {
      orderFinded.products.forEach((product) => {
        if (product.quantity + value > inventory && product.sku === sku) {
          stockOk = false;
        }
      });
    }
    return stockOk;
  }

  function changeQuantityItem(value: number) {
    if (config && config.content.pdv.showOutStock) {
      setQuantity(value > 0 ? value : 0);
    } else {
      const stockOk = verifyMaxStockWithCart(value);
      if (!stockOk) {
        Swal.fire({
          icon: "warning",
          title: "Estoque máximo atingido",
          text: "Não será mais possível aumentar a quantidade do produto",
        });
      } else if (value <= inventory) {
        setQuantity(value > 0 ? value : 0);
      } else if (value > inventory) {
        Swal.fire({
          icon: "warning",
          title: "Estoque máximo atingido",
          text: "Não será mais possível aumentar a quantidade do produto",
        });
      }
    }
  }

  return (
    <div className="food-product">
      {loading ? (
        <div style={{ width: "100%", height: "calc(100vh)" }}>
          <Loading type="spinningBubbles" color="#FFF" width={64} height={64} />
        </div>
      ) : (
        <div className="content-food-product">
          {product && (
            <div style={{ width: "100%" }}>
              <div className="header-food-product">
                <div>
                  <div
                    className="header-food-product-back"
                    onClick={() => navigate(-1)}
                  >
                    <svg
                      width="29"
                      height="29"
                      viewBox="0 0 29 29"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="14.5" cy="14.5" r="14.5" fill="#C4C4C4" />
                      <path
                        d="M17 21L11 14.5L17 8"
                        stroke="#636363"
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div className="header-food-product-tite">
                    <span>{product.name.toUpperCase()}</span>
                  </div>
                  <div className="header-food-product-close">
                    {/* <svg
                      width="29"
                      height="29"
                      viewBox="0 0 29 29"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="14.5" cy="14.5" r="14.5" fill="#C4C4C4" />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M6.97855 9.02155L12.957 15L6.97855 20.9784L8.97137 22.9713L14.9498 16.9928L20.9283 22.9713L22.9211 20.9784L16.9426 15L22.9211 9.02155L20.9283 7.02874L14.9498 13.0072L8.97137 7.02874L6.97855 9.02155Z"
                        fill="#636363"
                      />
                    </svg> */}
                  </div>
                </div>
                <div className="header-food-product-image">
                  {loadingImage && <div className="no-image" />}

                  {imagePath && (
                    <img
                      src={imagePath}
                      style={loadingImage ? { width: 0, height: 0 } : {}}
                      alt=""
                      className={"image-25Rounded"}
                      onLoad={() => setLoadingImage(false)}
                    />
                  )}
                </div>
                <div className="header-food-product-text">
                  {/* <div>
                    <span>{product.name}</span>
                  </div> */}
                  <div>
                    <span>{product.description}</span>
                  </div>
                  <div>
                    <span className="price">
                      {product.type === "fractional" && fractional
                        ? floatToPrice(
                            calculatePriceFractionalToFractional(fractional)
                          )
                        : product.variations && product.variations.length > 0
                        ? floatToPrice(product.variations[0].price)
                        : floatToPrice(0)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="product-optional-list">
                {fractional && (
                  <div className="product-optional-item">
                    <div className="product-optional-item-title">
                      <div>
                        <span>Selecione no máximo {fractional.max} itens</span>
                        <>
                          <br />
                          {/* <p></p> */}
                        </>
                      </div>
                      <div>OBRIGATÓRIO</div>
                    </div>
                    <div className="product-optional-item-amount">
                      {fractional.items.map((item, j) => (
                        <div key={`product-optional-${j}`}>
                          <div>
                            <span>{item.name}</span> <br />
                            {fractional.showDescription && (
                              <>
                                <span className="description">
                                  {item.description}
                                </span>
                                <br />
                              </>
                            )}
                            {fractional.showPrice && (
                              <span className="price">
                                {floatToPrice(item.price ?? 0)}
                              </span>
                            )}
                          </div>
                          {validShift && (
                            <div className="change-quantity">
                              <QuantityButton
                                changeValue={() =>
                                  changeQuantityFractional(j, -1)
                                }
                              >
                                <svg
                                  width="13"
                                  height="2"
                                  viewBox="0 0 13 2"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <rect width="13" height="2" fill="#940C0C" />
                                </svg>
                              </QuantityButton>
                              <input
                                type="text"
                                value={item.quantity || 0}
                                onChange={() => {}}
                              />
                              <QuantityButton
                                changeValue={() =>
                                  changeQuantityFractional(j, 1)
                                }
                              >
                                <svg
                                  width="13"
                                  height="13"
                                  viewBox="0 0 13 13"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M5.57148 4.89334e-05V5.57148H4.89334e-05L4.89334e-05 7.42862H5.57148L5.57148 13H7.42862L7.42862 7.42862L13 7.42862V5.57148L7.42862 5.57148V4.89334e-05L5.57148 4.89334e-05Z"
                                    fill="#940C0C"
                                  />
                                </svg>
                              </QuantityButton>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {optionals.map((optional, i) => {
                  if (optional.items.length > 0) {
                    return (
                      <div
                        key={`product-optional-${i}`}
                        className="product-optional-item"
                      >
                        <div className="product-optional-item-title">
                          <div>
                            <span>{optional.name}</span>
                            {optional.description && (
                              <>
                                <p>{optional.description}</p>
                              </>
                            )}
                            {(optional.type === "checkbox" ||
                              optional.type === "amount") &&
                              optional.max && (
                                <>
                                  <p>
                                    Selecione no máximo {optional.max} unidades
                                  </p>
                                </>
                              )}
                          </div>
                          <div>
                            {optional.required ? "OBRIGATÓRIO" : "OPCIONAL"}
                          </div>
                        </div>
                        {optional.type === "checkbox" && (
                          <div className="product-optional-item-checkbox">
                            {optional.items
                              .filter(
                                (item) =>
                                  item.product &&
                                  item.product.variations &&
                                  item.product.variations.length > 0
                              )
                              .map((item, j) => (
                                <div key={`product-optional-${j}`}>
                                  <div>
                                    <span>{item.product.name}</span>
                                    <br />
                                    <span className="price">
                                      {floatToPrice(
                                        item.product.variations[0].price ?? 0
                                      )}
                                    </span>
                                  </div>
                                  <div onClick={() => selectCheckbox(i, j)}>
                                    {validShift && renderRadio(item.selected)}
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                        {optional.type === "amount" && (
                          <div className="product-optional-item-amount">
                            {optional.items
                              .filter(
                                (item) =>
                                  item.product &&
                                  item.product.variations &&
                                  item.product.variations.length > 0
                              )
                              .map((item, j) => (
                                <div key={`product-optional-${j}`}>
                                  <div>
                                    <span>{item.product.name}</span>
                                    <br />
                                    <span className="price">
                                      {floatToPrice(
                                        item.product.variations[0].price ?? 0
                                      )}
                                    </span>
                                  </div>
                                  {validShift && (
                                    <div className="change-quantity">
                                      <QuantityButton
                                        changeValue={() =>
                                          changeQuantity(i, j, -1)
                                        }
                                      >
                                        <svg
                                          width="13"
                                          height="2"
                                          viewBox="0 0 13 2"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <rect
                                            width="13"
                                            height="2"
                                            fill="#940C0C"
                                          />
                                        </svg>
                                      </QuantityButton>
                                      <input
                                        type="text"
                                        value={item.quantity || 0}
                                        onChange={() => {}}
                                      />
                                      <QuantityButton
                                        changeValue={() =>
                                          changeQuantity(i, j, 1)
                                        }
                                      >
                                        <svg
                                          width="13"
                                          height="13"
                                          viewBox="0 0 13 13"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M5.57148 4.89334e-05V5.57148H4.89334e-05L4.89334e-05 7.42862H5.57148L5.57148 13H7.42862L7.42862 7.42862L13 7.42862V5.57148L7.42862 5.57148V4.89334e-05L5.57148 4.89334e-05Z"
                                            fill="#940C0C"
                                          />
                                        </svg>
                                      </QuantityButton>
                                    </div>
                                  )}
                                </div>
                              ))}
                          </div>
                        )}
                        {optional.type === "radio" && (
                          <div className="product-optional-item-radio">
                            {optional.items
                              .filter(
                                (item) =>
                                  item.product &&
                                  item.product.variations &&
                                  item.product.variations.length > 0
                              )
                              .map((item, j) => (
                                <div key={`product-optional-${j}`}>
                                  <div>
                                    <span>{item.product.name}</span>
                                    <br />
                                    <span className="price">
                                      {floatToPrice(
                                        item.product.variations[0].price ?? 0
                                      )}
                                    </span>
                                  </div>
                                  <div onClick={() => selectRadio(i, j)}>
                                    {validShift && renderRadio(item.selected)}
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  } else return <></>;
                })}

                {config?.content.pdv.showObservation && (
                  <div className="container-observation">
                    <textarea
                      className="border-0 w-100 form-control fw-normal"
                      value={observation}
                      placeholder="Observação"
                      rows={3}
                      onChange={(event) => setObservation(event.target.value)}
                    ></textarea>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* <div
            className={`footer-food-product ${
              totalSuccess ? "total-success" : ""
            }`}
          >
            <div>
              <div>
                <svg
                  width="27"
                  height="24"
                  viewBox="0 0 27 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 3.19998H0.5V3.69998H1V3.19998ZM1 1V0.5H0.5V1H1ZM3.19998 3.19998L3.65174 2.98571L3.51622 2.69998H3.19998V3.19998ZM7.15995 11.5489L7.59787 11.7902L7.72143 11.566L7.61171 11.3346L7.15995 11.5489ZM5.67496 14.2439L6.10914 14.492L6.11288 14.4852L5.67496 14.2439ZM20.7998 17.4999V17.9999H21.2998V17.4999H20.7998ZM20.7998 15.2999H21.2998V14.7999H20.7998V15.2999ZM7.78694 15.0249L7.30187 14.9036L7.28694 14.9633V15.0249H7.78694ZM7.81994 14.8929L7.38223 14.6512L7.3507 14.7083L7.33487 14.7716L7.81994 14.8929ZM8.80994 13.0999V12.5999H8.51486L8.37223 12.8582L8.80994 13.0999ZM18.9299 11.9669L18.492 11.7254L18.4914 11.7265L18.9299 11.9669ZM22.8678 4.82797L23.3056 5.06948L23.3077 5.06576L22.8678 4.82797ZM22.8456 3.7394L23.2754 3.48387L23.2754 3.48387L22.8456 3.7394ZM21.8998 3.19998L21.901 2.69998H21.8998V3.19998ZM5.63096 3.19998L5.17845 3.41266L5.31349 3.69998H5.63096V3.19998ZM4.59697 1L5.04948 0.787319L4.91444 0.5H4.59697V1ZM1.5 3.19998V1H0.5V3.19998H1.5ZM3.19998 2.69998H1V3.69998H3.19998V2.69998ZM7.61171 11.3346L3.65174 2.98571L2.74822 3.41426L6.70819 11.7632L7.61171 11.3346ZM6.11288 14.4852L7.59787 11.7902L6.72203 11.3076L5.23704 14.0026L6.11288 14.4852ZM5.89996 15.2999C5.89996 15.0006 5.97689 14.7233 6.10908 14.492L5.24084 13.9958C5.02103 14.3805 4.89996 14.8292 4.89996 15.2999H5.89996ZM7.59995 16.9999C6.6661 16.9999 5.89996 16.2337 5.89996 15.2999H4.89996C4.89996 16.786 6.11381 17.9999 7.59995 17.9999V16.9999ZM20.7998 16.9999H7.59995V17.9999H20.7998V16.9999ZM20.2998 15.2999V17.4999H21.2998V15.2999H20.2998ZM8.06194 15.7999H20.7998V14.7999H8.06194V15.7999ZM7.28694 15.0249C7.28694 15.455 7.6318 15.7999 8.06194 15.7999V14.7999C8.18409 14.7999 8.28694 14.9027 8.28694 15.0249H7.28694ZM7.33487 14.7716L7.30187 14.9036L8.27202 15.1462L8.30502 15.0142L7.33487 14.7716ZM8.37223 12.8582L7.38223 14.6512L8.25765 15.1346L9.24765 13.3416L8.37223 12.8582ZM17.0049 12.5999H8.80994V13.5999H17.0049V12.5999ZM18.4914 11.7265C18.2037 12.2511 17.6442 12.5999 17.0049 12.5999V13.5999C18.0155 13.5999 18.908 13.0467 19.3683 12.2073L18.4914 11.7265ZM22.43 4.58647L18.492 11.7254L19.3677 12.2084L23.3056 5.06947L22.43 4.58647ZM22.4158 3.99494C22.5243 4.1774 22.5289 4.40345 22.428 4.59018L23.3077 5.06576C23.5759 4.5695 23.5637 3.96876 23.2754 3.48387L22.4158 3.99494ZM21.8987 3.69998C22.111 3.70047 22.3073 3.81249 22.4158 3.99494L23.2754 3.48387C22.9871 2.99897 22.4651 2.70128 21.901 2.69998L21.8987 3.69998ZM5.63096 3.69998H21.8998V2.69998H5.63096V3.69998ZM4.14446 1.21268L5.17845 3.41266L6.08347 2.9873L5.04948 0.787319L4.14446 1.21268ZM1 1.5H4.59697V0.5H1V1.5ZM5.91139 20.8C5.91139 19.8629 6.66978 19.1001 7.60037 19.1001V18.1001C6.11098 18.1001 4.91139 19.3172 4.91139 20.8H5.91139ZM7.60037 22.5C6.66978 22.5 5.91139 21.7372 5.91139 20.8H4.91139C4.91139 22.2829 6.11098 23.5 7.60037 23.5V22.5ZM9.30035 20.8C9.30035 21.7339 8.53422 22.5 7.60037 22.5V23.5C9.0865 23.5 10.3004 22.2862 10.3004 20.8H9.30035ZM7.60037 19.1001C8.53422 19.1001 9.30035 19.8662 9.30035 20.8H10.3004C10.3004 19.3139 9.0865 18.1001 7.60037 18.1001V19.1001ZM18.6002 18.1001C17.1108 18.1001 15.9112 19.3172 15.9112 20.8H16.9112C16.9112 19.8629 17.6696 19.1001 18.6002 19.1001V18.1001ZM21.3001 20.8C21.3001 19.3139 20.0863 18.1001 18.6002 18.1001V19.1001C19.534 19.1001 20.3001 19.8662 20.3001 20.8H21.3001ZM18.6002 23.5C20.0863 23.5 21.3001 22.2862 21.3001 20.8H20.3001C20.3001 21.7339 19.534 22.5 18.6002 22.5V23.5ZM15.9112 20.8C15.9112 22.2829 17.1108 23.5 18.6002 23.5V22.5C17.6696 22.5 16.9112 21.7372 16.9112 20.8H15.9112Z"
                    fill={totalSuccess ? "white" : "#636363"}
                  />
                  <circle
                    cx="20.0001"
                    cy="12.44"
                    r="5.66"
                    fill={totalSuccess ? "white" : "#636363"}
                    stroke={totalSuccess ? "#511212" : "#DADADA"}
                  />
                  <path
                    d="M20.5849 17.1599V11.8359C20.5849 11.7112 20.5849 11.5792 20.5849 11.4399C20.5923 11.2932 20.5996 11.1429 20.6069 10.9889C20.3576 11.2309 20.0679 11.4436 19.7379 11.6269C19.4079 11.8029 19.0706 11.9312 18.7259 12.0119L18.4949 11.2089C18.6269 11.1942 18.7919 11.1466 18.9899 11.0659C19.1953 10.9779 19.4079 10.8716 19.6279 10.7469C19.8553 10.6149 20.0643 10.4756 20.2549 10.3289C20.4456 10.1822 20.5923 10.0392 20.6949 9.89991H21.4649V17.1599H20.5849Z"
                    fill={totalSuccess ? "#511212" : "#DADADA"}
                  />
                </svg>
              </div>
              <div onClick={() => navigate(`/${pluginspace_id}/cart`)}>ver carrinho</div>
              <div>{floatToPrice(price)}</div>
            </div>
          </div>  */}
          {config &&
          config?.content &&
          config?.content.pdv &&
          config?.content.pdv.noSales ? (
            <></>
          ) : (
            validShift && (
              <div className="add-cart-food">
                <div className="quantity-product-food">
                  <div className="change-quantity">
                    <QuantityButton
                      changeValue={() => changeQuantityItem(quantity - 1)}
                    >
                      <svg
                        width="14"
                        height="2"
                        viewBox="0 0 14 2"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect width="14" height="2" fill="#ABABAB" />
                      </svg>
                    </QuantityButton>
                    <input
                      type="text"
                      onChange={(event) =>
                        changeQuantityItem(parseInt(event.target.value))
                      }
                      value={quantity > 0 ? quantity : ""}
                    />
                    <QuantityButton
                      changeValue={() => changeQuantityItem(quantity + 1)}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M6 0V6H0V8H6V14H8V8H14V6H8V0H6Z"
                          fill="#ABABAB"
                        />
                      </svg>
                    </QuantityButton>
                  </div>
                  {quantity > 0 && (
                    <button
                      onClick={() => addCart()}
                      style={{
                        color: config?.webappcolor.fontbutton
                          ? config.webappcolor.fontbutton
                          : "#636363",
                        backgroundColor: totalSuccess
                          ? config?.webappcolor.webappbutton
                            ? config.webappcolor.webappbutton
                            : "#EAEAEA"
                          : "#EAEAEA",
                      }}
                      className="button-product-food"
                      disabled={!totalSuccess}
                    >
                      <p>ADICIONAR</p>
                      <p>{calculatePrice()}</p>
                    </button>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      )}
      <ModalProdutoCarrinho
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        product={productModal}
      />
    </div>
  );
}
